/**
 * USER MANAGER SYSTEM
 * Handles user profiles, authentication, and data persistence
 */

const UserManager = {
    // State
    currentUser: null,
    users: [],

    // Constants
    STORAGE_KEYS: {
        USERS: 'math_detectives_users',
        CURRENT_USER: 'math_detectives_current_user',
        PREFIX_USER: 'user_',
        PREFIX_GUEST: 'guest_'
    },

    /**
     * Initialize the system
     */
    init() {
        this.loadUsers();
        console.log('👤 UserManager initialized. Users loaded:', this.users.length);

        // Expose under Detectimaticas namespace with legacy fallback
        if (typeof window !== 'undefined') {
            window.Detectimaticas = window.Detectimaticas || {};
            window.Detectimaticas.UserManager = this;
            window.UserManager = this;
        }
    },

    /**
     * Load all registered users
     */
    loadUsers() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEYS.USERS);
            this.users = stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.error('Error loading users:', e);
            this.users = [];
        }
    },

    /**
     * Create a new user profile
     * @param {string} name - User's name
     * @param {string} avatarId - Avatar identifier
     */
    createUser(name, avatarId) {
        // Generate unique ID
        const id = 'user_' + Date.now() + '_' + Math.floor(Math.random() * 1000);

        const newUser = {
            id: id,
            name: name,
            avatar: avatarId,
            pin: '1234', // Default Parental PIN
            group: '1A', // Default Group
            created: new Date().toISOString(),
            lastLogin: null
        };

        this.users.push(newUser);
        this.saveUsersList();

        console.log(`👤 New user created: ${name} (${id})`);
        return newUser;
    },

    /**
     * Get Teacher PIN (default 1234)
     */
    getTeacherPin() {
        return localStorage.getItem('math_detectives_teacher_pin') || '1234';
    },

    /**
     * Set Teacher PIN
     */
    setTeacherPin(newPin) {
        localStorage.setItem('math_detectives_teacher_pin', newPin);
    },

    /**
     * Update User PIN
     */
    updateUserPin(userId, newPin) {
        const user = this.users.find(u => u.id === userId);
        if (user) {
            user.pin = newPin;
            this.saveUsersList();
            return true;
        }
        return false;
    },

    /**
    * Update User Group
    */
    updateUserGroup(userId, newGroup) {
        const user = this.users.find(u => u.id === userId);
        if (user) {
            user.group = newGroup;
            this.saveUsersList();
            return true;
        }
        return false;
    },

    /**
     * Save the list of users to localStorage
     */
    saveUsersList() {
        localStorage.setItem(this.STORAGE_KEYS.USERS, JSON.stringify(this.users));
    },

    /**
     * Log in a specific user
     * @param {string} userId - ID of the user to login
     */
    loginUser(userId) {
        const user = this.users.find(u => u.id === userId);
        if (!user) {
            console.error('User not found:', userId);
            return false;
        }

        this.currentUser = { ...user, type: 'registered' };

        // Update last login
        user.lastLogin = new Date().toISOString();
        this.saveUsersList();

        console.log(`🔓 Logged in as: ${user.name}`);
        return true;
    },

    /**
     * Start a Guest session (no persistence across sessions)
     */
    loginGuest() {
        this.currentUser = {
            id: 'guest_' + Date.now(),
            name: 'Invitado',
            avatar: 'guest',
            type: 'guest',
            created: new Date().toISOString()
        };

        console.log('🔓 Logged in as Guest');
        return true;
    },

    /**
     * Log out current user
     */
    logout() {
        if (this.currentUser && this.currentUser.type === 'guest') {
            // Cleanup guest data if feasible/needed (sessionStorage clears itself on close)
            sessionStorage.clear();
        }

        this.currentUser = null;
        console.log('🔒 Logged out');

        // Return to login screen
        if (typeof showLoginScreen === 'function') {
            showLoginScreen();
        } else {
            location.reload();
        }
    },

    /**
     * Get a namespaced storage key
     */
    getStorageKey(key) {
        if (!this.currentUser) return key; // Fallback
        return `${this.currentUser.id}_${key}`;
    },

    /**
     * Save data (Persistent for User, Session for Guest)
     */
    saveData(key, value) {
        if (!this.currentUser) return;

        if (value && typeof value === 'object' && window.generateChecksum) {
            value._signature = window.generateChecksum(value);
        }

        const storageKey = this.getStorageKey(key);
        const data = JSON.stringify(value);

        try {
            if (this.currentUser.type === 'guest') {
                sessionStorage.setItem(storageKey, data);
            } else {
                localStorage.setItem(storageKey, data);
            }
        } catch (e) {
            console.warn('Storage failed (quota exceeded or incognito mode). Data not saved.', e);
        }
    },

    /**
     * Load data for a specific user (Persistent only)
     */
    loadUserData(userId, key) {
        const storageKey = `${userId}_${key}`;
        try {
            const data = localStorage.getItem(storageKey);
            if (!data) return null;
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed === 'object' && window.verifyIntegrity && !window.verifyIntegrity(parsed)) {
                console.warn('⚠️ Integredad no válida en datos almacenados de usuario:', userId, key);
            }
            return parsed;
        } catch (e) {
            console.warn('Storage read failed.', e);
            return null;
        }
    },

    /**
     * Save data for a specific user (Persistent only)
     */
    saveUserData(userId, key, value) {
        if (!userId) return;
        if (value && typeof value === 'object' && window.generateChecksum) {
            value._signature = window.generateChecksum(value);
        }
        const storageKey = `${userId}_${key}`;
        const data = JSON.stringify(value);
        localStorage.setItem(storageKey, data);
    },

    /**
     * Load data (Persistent for User, Session for Guest)
     */
    loadData(key) {
        if (!this.currentUser) return null;

        const storageKey = this.getStorageKey(key);
        let data;

        if (this.currentUser.type === 'guest') {
            data = sessionStorage.getItem(storageKey);
        } else {
            data = localStorage.getItem(storageKey);
        }

        try {
            if (!data) return null;
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed === 'object' && window.verifyIntegrity && !window.verifyIntegrity(parsed)) {
                console.warn('⚠️ Datos manipulados externamente detectados para:', key);
            }
            return parsed;
        } catch (e) {
            console.error('Error parsing data:', e);
            return null;
        }
    },

    /**
     * Check if a user is currently logged in
     */
    isLoggedIn() {
        return !!this.currentUser;
    },

    /**
     * Get current user name
     */
    getCurrentUserName() {
        return this.currentUser ? this.currentUser.name : '';
    },

    /**
     * Delete a user profile (Admin function)
     */
    deleteUser(userId) {
        // 1. Remove from list
        this.users = this.users.filter(u => u.id !== userId);
        this.saveUsersList();

        // 2. Clear their data from localStorage
        // This is tricky because keys are scattered. 
        // We'll iterate all keys and remove those starting with the ID.
        const prefix = `${userId}_`;

        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
                localStorage.removeItem(key);
            }
        });

        console.log(`🗑️ Deleted user: ${userId}`);
    }
};

// Initialize immediately? Or wait for app? 
// Usually wait for app, but init logic is safe to run.
// We'll let app.js call init()

// Expose globally
window.UserManager = UserManager;
